<template>
  <div>
    <Combobox
      :id="id"
      ref="combobox"
      v-model="selected"
      v-model:query="query"
      :search="search"
      :labels="labels"
      :min-query-length="MIN_PLACE_QUERY_LENGTH"
      :debounce-ms="PLACE_SEARCH_DEBOUNCE_MS"
      :disabled="disabled"
      :placeholder="placeholder ?? t('placePicker.placeholder')"
      @select="onSelect"
      @clear="onClear"
    />
    <!--
      The typed-but-unmatched case is not an error and must not read like one:
      the name stays exactly as typed and the unit saves without a place.
    -->
    <p v-if="showKeptHint" class="lumi-place-picker__hint">{{ t('placePicker.keptHint') }}</p>
    <!-- No GeoNames credit here: the CC BY 4.0 attribution is given once, in
         workspace-ui's Preferences ("Open data attributions") — see
         people-api/db/places/README.md. -->
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import type { AxiosInstance } from 'axios';
import { useI18n } from 'vue-i18n';
import { Combobox, ComboboxRateLimited, countryName, type ComboboxLabels, type ComboboxOption } from '@lumi/ui';
import {
  MIN_PLACE_QUERY_LENGTH,
  PLACE_SEARCH_DEBOUNCE_MS,
  PLACE_SEARCH_LIMIT,
  type PickedPlace,
  type PlaceSearchResult,
} from '../utils/places';

// The place picker (D13, issue 57): a location's optional link to a real
// settlement in the shipped GeoNames extract, searched through people-api's
// `GET /places/search`. The host injects the client that reaches it (`api`) —
// app-kit holds no client of its own (ADR 0026).
//
// Two ways to use it:
//  - a form field (people's org-unit edit): `v-model` is the place, the field
//    shows the picked name, and the hint says typed text is kept without a place.
//  - a name field (the onboarding wizard): `text-is-name` + `v-model:text`. The
//    typed text IS the location's name, a pick is handed up as `update:modelValue`
//    and the host adds it straight away, leaving the picker empty (it keeps no
//    selection the host did not store). A name that matches nothing is not an error.

const props = defineProps<{
  api: Pick<AxiosInstance, 'get'>;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  /** The typed text is the host's own value (a location name) rather than a search for a place. */
  textIsName?: boolean;
}>();
const model = defineModel<PickedPlace | null>({ default: null });
/** The typed query. Optional: a host that does not bind it gets a private one. */
const query = defineModel<string>('text', { default: '' });

const { t, locale } = useI18n();

function toOption(place: PickedPlace): ComboboxOption {
  return { value: place.placeId, label: place.placeName, hint: countryName(place.placeCountryCode, locale.value) };
}

const selected = ref<ComboboxOption | null>(model.value ? toOption(model.value) : null);
if (model.value && !query.value) query.value = model.value.placeName;

// The parent may load the unit after this mounts (edit modal opened on a fetch).
watch(model, (next) => {
  selected.value = next ? toOption(next) : null;
  if (next) query.value = next.placeName;
});

const showKeptHint = computed(() => !props.textIsName && !selected.value && query.value.trim().length > 0);

const labels = computed<ComboboxLabels>(() => ({
  searching: t('placePicker.searching'),
  typeMore: t('placePicker.typeMore', { count: MIN_PLACE_QUERY_LENGTH }),
  noMatch: t(props.textIsName ? 'placePicker.noMatchAdd' : 'placePicker.noMatch'),
  error: t('placePicker.error'),
  retry: t('common.retry'),
  rateLimited: t('placePicker.rateLimited'),
  // A function, not a template: several product languages pluralize this.
  resultsAnnouncement: (count: number) => t('placePicker.resultsAnnouncement', count),
  clear: t('placePicker.clear'),
}));

function describe(row: PlaceSearchResult): ComboboxOption {
  // GeoNames' `name` is the internationally common form ("Munich", "Warsaw"), so
  // the localized name wins when the dataset has one — otherwise a German user
  // would be shown an English city.
  const label = row.localizedName || row.name;
  const parts = [countryName(row.countryCode, locale.value)];
  // Why this row is in the list at all when the user typed another language's
  // name for it ("Monachium" -> München).
  if (row.matchedName && row.matchedName !== label) parts.push(row.matchedName);
  if (row.population > 0) parts.push(new Intl.NumberFormat(locale.value).format(row.population));
  return { value: row.placeId, label, hint: parts.join(' · '), data: row };
}

async function search(term: string, signal: AbortSignal): Promise<ComboboxOption[]> {
  try {
    const res = await props.api.get('/places/search', {
      params: { q: term, locale: locale.value, limit: PLACE_SEARCH_LIMIT },
      signal,
      // The combobox renders its own inline states; the global toast would be a
      // second voice for the same failure.
      suppressGlobalError: true,
    });
    const rows: PlaceSearchResult[] = res.data?.results ?? [];
    return rows.map(describe);
  } catch (err: any) {
    // A 429 is a pause, not a failure (ADR 0046): the combobox waits it out and
    // re-runs the same query. Rendering it as an error — or worse, as "no
    // match" — would push a fast typist onto the keep-what-you-typed path and
    // lose them the place they were looking for.
    if (err?.response?.status === 429) {
      const retryAfter = Number(err.response?.data?.retryAfter ?? err.response?.headers?.['retry-after'] ?? 1);
      throw new ComboboxRateLimited(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1);
    }
    throw err;
  }
}

function onSelect(option: ComboboxOption) {
  const row = option.data as PlaceSearchResult;
  model.value = {
    placeId: row.placeId,
    placeName: option.label,
    placeCountryCode: row.countryCode,
    placeLatitude: row.latitude,
    placeLongitude: row.longitude,
  };
  // The field shows what the host STORED: a host that took the pick and left the
  // model empty (it added a chip) must not be left showing a selection.
  void nextTick(() => {
    selected.value = model.value ? toOption(model.value) : null;
  });
}

function onClear() {
  model.value = null;
}

const combobox = ref<{ close: () => void } | null>(null);
// Enter / the host's own add took the typed text: shut the result list it was showing.
function close() {
  combobox.value?.close();
}

defineExpose({ close });
</script>

<style>
.lumi-place-picker__hint{margin:var(--space-1) 0 0;font-size:var(--text-xs);color:var(--text-tertiary);}
</style>
